import React from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'
import { isMobileAgent } from 'src/utils/string'

interface ResponsiveContext {
  mobile
  tablet
  desktop
}

const matchMedia = (prop, value) =>
  typeof window !== 'undefined' &&
  window.matchMedia(`(${prop}: ${value}px)`).matches

const DEFAULT_BREAKPOINTS = {
  mobile: 0,
  tablet: 720,
  desktop: 1024
}

export const ResponsiveContext = React.createContext({} as ResponsiveContext)

export const withResponsiveContext = (Component) => {
  const EnhancedComponent = (props) => (
    <ResponsiveContext.Consumer>
      {(responsiveProps) => (
        <Component {...props} responsive={responsiveProps} />
      )}
    </ResponsiveContext.Consumer>
  )
  hoistNonReactStatic(EnhancedComponent, Component)
  return EnhancedComponent
}

export const ResponsiveContextProvider = React.memo(
  ({ userAgent, children }: {
    userAgent: string
    children: unknown
  }) => {
    const [responsiveState, setResponsiveState] = React.useState(() => {
      const isMobile = isMobileAgent(userAgent)
      return {
        mobile: isMobile,
        tablet: isMobile,
        desktop: !isMobile
      }
    })

    const handleResize = React.useCallback(() => {
      const mobile = matchMedia('max-width', DEFAULT_BREAKPOINTS.tablet - 1)
      const tablet =
        matchMedia('min-width', DEFAULT_BREAKPOINTS.tablet) &&
        matchMedia('max-width', DEFAULT_BREAKPOINTS.desktop - 1)
      const desktop = matchMedia('min-width', DEFAULT_BREAKPOINTS.desktop)

      if (
        mobile !== responsiveState.mobile ||
        tablet !== responsiveState.tablet ||
        desktop !== responsiveState.desktop
      ) {
        setResponsiveState({ mobile, tablet, desktop })
      }
    }, [
      responsiveState.desktop,
      responsiveState.mobile,
      responsiveState.tablet
    ])

    React.useEffect(() => {
      window.addEventListener('resize', handleResize)
      handleResize()
      return () => window.removeEventListener('resize', handleResize)
    }, [handleResize])

    return (
      <ResponsiveContext.Provider value={responsiveState}>
        {children}
      </ResponsiveContext.Provider>
    )
  }
)

const withResponsiveProps = (Component, responsiveKeys = []) => {
  const getComponentName = (Component) =>
    Component.displayName || Component.name || 'Component'

  const EnhancedComponent = (props) => (
    <ResponsiveContext.Consumer>
      {({ mobile, tablet, desktop }: {
        mobile: boolean
        tablet: boolean
        desktop: boolean
      }) => {
        const responsiveProps = {}
        const screenSize = mobile
          ? 'mobile'
          : tablet
          ? 'tablet'
          : desktop
          ? 'desktop'
          : null
        responsiveKeys.forEach((key) => {
          if (props[key] && props[key][screenSize] !== undefined)
            responsiveProps[key] = props[key][screenSize]
        })
        return <Component {...props} {...responsiveProps} />
      }}
    </ResponsiveContext.Consumer>
  )
  hoistNonReactStatic(EnhancedComponent, Component)
  EnhancedComponent.displayName = `WithResponsiveProps(${getComponentName(
    Component
  )})`
  return EnhancedComponent
}

export default withResponsiveProps
